import React from 'react';

export const ENTITY_TYPES = [
    { value: 'Device', label: 'Device' },
    { value: 'Host', label: 'Host' },
];

export const DEVICE_METRICS = [
    { value: 'deviceCPUUsage', label: 'CPU usage', type: 'numeric' },
    { value: 'deviceMemoryUsage', label: 'Memory usage', type: 'numeric' },
    { value: 'deviceStorageUsage', label: 'Disk usage', type: 'numeric' },
    { value: 'uptime', label: 'Uptime', type: 'numeric' },
    { value: 'batteryHealth', label: 'Battery health', type: 'numeric' },
    { value: 'temperature', label: 'Temperature', type: 'numeric' },
];

export const HOST_METRICS = [
    { value: 'systemStatus', label: 'System status', type: 'status' },
    { value: 'uptime', label: 'Uptime', type: 'numeric' },
    { value: 'cpuUsage', label: 'CPU usage', type: 'numeric' },
    { value: 'memoryUsage', label: 'Memory usage', type: 'numeric' },
    { value: 'diskUsage', label: 'Disk usage', type: 'numeric' },
    { value: 'greengrassStatus', label: 'Greengrass status', type: 'status' },
    { value: 'parentReachable', label: 'Network availability', type: 'status' },
    { value: 'diskIoWait', label: 'Disk I/O wait', type: 'numeric' },
    { value: 'usbConnections', label: 'USB number of connections', type: 'numeric' },
];

// Derived from metric definitions — single source of truth
export const NUMERIC_METRICS = [...DEVICE_METRICS, ...HOST_METRICS]
    .filter((m) => m.type === 'numeric')
    .map((m) => m.value);

export const AGGREGATIONS = [
    { value: 'avg', label: 'Average (avg)' },
    { value: 'max', label: 'Maximum (max)' },
    { value: 'min', label: 'Minimum (min)' },
    { value: 'latest', label: 'Latest value' },
];

// Aggregations for status metrics — only latest makes sense
export const STATUS_AGGREGATIONS = [
    { value: 'latest', label: 'Latest value' },
];

export const OPERATORS = [
    { value: 'gt', label: 'Greater than (>)' },
    { value: 'lt', label: 'Less than (<)' },
    { value: 'eq', label: 'Equals (=)' },
    { value: 'neq', label: 'Not equals (≠)' },
    { value: 'gte', label: 'Greater than or equal (≥)' },
    { value: 'lte', label: 'Less than or equal (≤)' },
];

// Operators for status metrics (adb_status, greengrass_status, etc.) — only equals/not equals make sense
export const STATUS_OPERATORS = [
    { value: 'eq', label: 'Equals (=)' },
    { value: 'neq', label: 'Not equals (≠)' },
];

// Maps for readable display in table
const OPERATOR_WORDS = {
    gt: 'Greater than',
    lt: 'Less than',
    eq: 'Equals to',
    neq: 'Not equals to',
    gte: 'Greater than or equal to',
    lte: 'Less than or equal to',
};

const AGGREGATION_LABELS = {
    avg: 'Average',
    max: 'Maximum',
    min: 'Minimum',
    latest: 'Latest',
};

// Maps metric value to its label for display
const ALL_METRICS = [...DEVICE_METRICS, ...HOST_METRICS];
const getMetricLabel = (value) => {
    const found = ALL_METRICS.find((m) => m.value === value);
    return found ? found.label : value;
};

export const ALARM_RULE_COLUMNS = [
    {
        id: 'metric',
        header: 'Metric',
        cell: (e) => getMetricLabel(e.metric),
        width: 180,
    },
    {
        id: 'entityType',
        header: 'Entity type',
        cell: (e) => e.entityType,
        width: 100,
    },
    {
        id: 'aggregation',
        header: 'Aggregation',
        cell: (e) => AGGREGATION_LABELS[e.aggregation] || e.aggregation,
        width: 120,
    },
    {
        id: 'condition',
        header: 'Condition',
        cell: (e) => {
            const word = OPERATOR_WORDS[e.operator] || e.operator;
            const isNumeric = NUMERIC_METRICS.includes(e.metric);
            return isNumeric ? `${word} ${e.threshold}` : `${word} "${e.threshold}"`;
        },
        width: 250,
    },
];

// UI Strings
export const UI_STRINGS = {
    // AlarmRulesTable
    TABLE_TITLE: 'Alarm rules',
    TABLE_EMPTY_TITLE: 'No alarm rules configured',
    TABLE_EMPTY_SUBTITLE: 'Create an alarm rule to start monitoring device and host health metrics for this lab.',
    TABLE_LOADING: 'Loading alarm rules',
    TABLE_ADD_BUTTON: '+ Add rule',
    TABLE_EDIT_BUTTON: 'Edit',

    // AlarmRuleForm
    FORM_TITLE_CREATE: 'Add alarm rule',
    FORM_TITLE_EDIT: 'Edit alarm rule',
    FORM_CREATE: 'Create',
    FORM_SAVE: 'Save',

    // Shared
    DESCRIPTION: 'Define the conditions that trigger alarms for this lab. When a condition is breached, tickets will be sent to the configured lab CTI.',
    CANCEL: 'Cancel',
    DELETE: 'Delete',
    SELECT_METRIC_FIRST: 'Select a metric first',

    // Field labels and descriptions
    ENTITY_TYPE_LABEL: 'Entity type',
    ENTITY_TYPE_DESCRIPTION: 'Whether this rule applies to devices or hosts',
    METRIC_LABEL: 'Metric',
    METRIC_DESCRIPTION: 'The health metric to monitor for this alarm rule',
    METRIC_PLACEHOLDER: 'Select a metric',
    AGGREGATION_LABEL: 'Aggregation',
    AGGREGATION_DESCRIPTION: 'How to summarize the metric value over the one-hour evaluation window. Options: avg (average), max (highest spike), min (lowest dip), latest (most recent value)',
    AGGREGATION_PLACEHOLDER: 'Select an aggregation',
    OPERATOR_LABEL: 'Operator',
    OPERATOR_DESCRIPTION: 'How to compare the metric value against the threshold',
    OPERATOR_PLACEHOLDER: 'Select an operator',
    THRESHOLD_LABEL: 'Threshold',
    THRESHOLD_DESCRIPTION_NUMERIC: 'The numeric value that triggers the alarm (e.g., 80 for 80% CPU usage)',
    THRESHOLD_DESCRIPTION_STATUS: 'The status value to compare against (e.g., connected, active, running)',
    THRESHOLD_PLACEHOLDER_NUMERIC: 'Enter a number (e.g., 80)',
    THRESHOLD_PLACEHOLDER_STATUS: 'Enter a status value (e.g., connected)',

    // DeleteAlarmRuleModal
    DELETE_MODAL_TITLE: 'Delete alarm rule',
    DELETE_MODAL_MESSAGE: 'Are you sure you want to delete this alarm rule?',

    // Error messages
    ERROR_DELETE: 'Failed to delete alarm rule, try again later or contact @ds2-device-farm-dev.',
    ERROR_SAVE: 'Failed to save alarm rules, try again later or contact @ds2-device-farm-dev',
};
